import React, { useEffect, useState } from 'react';
import { testCSPImplementation } from '../utils/cspTest';
import { testFirebaseRules } from '../utils/firebaseTest';
import styled from 'styled-components';

const SecurityTestContainer = styled.div`
  max-width: 100%;
`;

const TestResults = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
`;

const TestList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TestItem = styled.li`
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1rem;
  padding: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
`;

const ErrorList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
  color: #dc3545;
`;

const ErrorItem = styled.li`
  margin: 0.5rem 0;
  padding: 0.75rem;
  background: #fff;
  border-left: 4px solid #dc3545;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const TestSection = styled.div`
  margin-bottom: 1.5rem;

  h3 {
    margin: 0 0 1rem 0;
    color: var(--primary-color);
    font-size: 1.1rem;
  }

  h4 {
    margin: 1.5rem 0 0.75rem 0;
    color: #666;
    font-size: 1rem;
  }
`;

const SecurityTest = () => {
  const [testResults, setTestResults] = useState({
    csp: null,
    firebase: null
  });

  useEffect(() => {
    // Test CSP
    const cspResults = testCSPImplementation();
    setTestResults(prev => ({ ...prev, csp: cspResults }));

    // Test Firebase Rules
    const testFirebase = async () => {
      try {
        const firebaseResults = await testFirebaseRules();
        setTestResults(prev => ({ ...prev, firebase: firebaseResults }));
      } catch (error) {
        console.error('Firebase tests failed:', error);
      }
    };
    testFirebase();
  }, []);

  if (!testResults.csp) return null;

  return (
    <SecurityTestContainer>
      {/* CSP Test Results */}
      <TestSection>
        <h3>CSP Resource Loading</h3>
        <TestList>
          <TestItem>Scripts: {testResults.csp.resources.scripts ? '✅' : '❌'}</TestItem>
          <TestItem>Styles: {testResults.csp.resources.styles ? '✅' : '❌'}</TestItem>
          <TestItem>Images: {testResults.csp.resources.images ? '✅' : '❌'}</TestItem>
          <TestItem>Fonts: {testResults.csp.resources.fonts ? '✅' : '❌'}</TestItem>
          <TestItem>Firebase: {testResults.csp.resources.firebase ? '✅' : '❌'}</TestItem>
        </TestList>
        
        {testResults.csp.errors.length > 0 && (
          <TestResults>
            <h3>CSP Errors</h3>
            <ErrorList>
              {testResults.csp.errors.map((error, index) => (
                <ErrorItem key={index}>{error}</ErrorItem>
              ))}
            </ErrorList>
          </TestResults>
        )}
      </TestSection>

      {/* Firebase Test Results */}
      {testResults.firebase && (
        <TestSection>
          <h3>Firebase Security</h3>
          
          <h4>Authentication</h4>
          <TestList>
            <TestItem>Signup: {testResults.firebase.auth.signup ? '✅' : '❌'}</TestItem>
            <TestItem>Login: {testResults.firebase.auth.login ? '✅' : '❌'}</TestItem>
            <TestItem>Logout: {testResults.firebase.auth.logout ? '✅' : '❌'}</TestItem>
          </TestList>

          <h4>Firestore</h4>
          <TestList>
            <TestItem>Read: {testResults.firebase.firestore.read ? '✅' : '❌'}</TestItem>
            <TestItem>Write: {testResults.firebase.firestore.write ? '✅' : '❌'}</TestItem>
            <TestItem>Delete: {testResults.firebase.firestore.delete ? '✅' : '❌'}</TestItem>
          </TestList>

          <h4>Storage</h4>
          <TestList>
            <TestItem>Upload: {testResults.firebase.storage.upload ? '✅' : '❌'}</TestItem>
            <TestItem>Download: {testResults.firebase.storage.download ? '✅' : '❌'}</TestItem>
            <TestItem>Delete: {testResults.firebase.storage.delete ? '✅' : '❌'}</TestItem>
          </TestList>

          {testResults.firebase.errors.length > 0 && (
            <TestResults>
              <h3>Firebase Errors</h3>
              <ErrorList>
                {testResults.firebase.errors.map((error, index) => (
                  <ErrorItem key={index}>{error}</ErrorItem>
                ))}
              </ErrorList>
            </TestResults>
          )}
        </TestSection>
      )}
    </SecurityTestContainer>
  );
};

export default SecurityTest; 